import * as Api from '@ViewModels';
import { BoardItemActivityList } from '@WebComponents/boards/BoardItemActivityList';
import { CompanyContactsList } from '@WebComponents/companies/CompanyContactsList';
import { EditOpportunityModal } from '@WebComponents/dataBoards/opportunities/EditOpportunity';
import { OpportunityProfile } from '@WebComponents/dataBoards/opportunities/OpportunityProfile';
import { EntityRichContent } from '@WebComponents/entities/EntityRichContent';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { NotFound } from '@WebComponents/NotFound';
import { SplitView } from '@WebComponents/SplitView';
import { css, StyleDeclarationValue } from 'aphrodite';
import { Tabs } from 'radix-ui';
import * as React from 'react';
import { Redirect, useLocation, useRouteMatch } from 'react-router';
import { NavLink } from 'react-router-dom';
import { toast } from 'sonner';
import { ILocationState } from '../../../../models';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import {
	useAutomationTemplateIdQuery,
	useGetBoard,
	useGetOpportunity,
	useInfiniteBoardsQuery,
	useStartAutomationForContactMutation,
} from '../../../../queries';
import { CompanyViewModel, ContactViewModel, IOpportunity } from '../../../../viewmodels/AppViewModels';
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from '../../../components/Breadcrumb';
import { Button } from '../../../components/Button';
import { Modal } from '../../../components/Modal';

import { MultiContainerHeader } from '../../../components/MultiContainerHeader';
import { styleSheet } from './styles';

enum OpportunityDetailTabs {
	Activity = 'Activity',
	Notes = 'Notes',
	Contacts = 'Contacts',
}

const _Opportunity = ({
	className,
	routeContainerClassName,
	styles,
}: {
	className?: string;
	routeContainerClassName?: string;
	styles?: StyleDeclarationValue[];
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const [showingEditModal, setShowingEditModal] = React.useState(false);
	const [pendingAutomationTemplateId, setPendingAutomationTemplateId] = React.useState<string | null>(null);
	const [triggeringOpportunity, setTriggeringOpportunity] = React.useState<Api.IOpportunity | null>(null);

	const match = useRouteMatch<{
		id: string;
		boardId: string;
	}>();
	const loc = useLocation();
	const { id: opportunityId, boardId } = match.params;

	const boardsQuery = useInfiniteBoardsQuery({
		type: 'Opportunity',
		expand: 'Stage.Tag',
		autoFetchAll: true,
		meta: {
			toastErrorMessage: 'Error loading boards',
			errorLogging: { action: 'OpportunityDetailInfiniteBoardsQuery-Error', category: 'Opportunities' },
		},
	});
	const boards = React.useMemo(() => boardsQuery.data?.pages.map(page => page.values).flat() ?? [], [boardsQuery.data]);

	const opportunityQuery = useGetOpportunity({
		opportunityId,
	});
	const opportunity = opportunityQuery.data;

	const getBoardQuery = useGetBoard({
		boardId,
	});

	const stagesWithAutomations = React.useMemo<Api.IAccountTag[]>(() => {
		return boards.flatMap((board: Api.IBoard) =>
			(board.stages ?? [])
				.filter(
					(stage: Api.IBoardStageReference): stage is Api.IBoardStageReference & { tag: Api.IAccountTag } =>
						!!stage.tag?.automationTemplateId
				)
				.map((stage: Api.IBoardStageReference & { tag: Api.IAccountTag }) => stage.tag)
		);
	}, [boards]);

	const getAutomationTemplateByIdQuery = useAutomationTemplateIdQuery({
		enabled: !!pendingAutomationTemplateId,
		id: pendingAutomationTemplateId,
	});
	const automationTemplate = getAutomationTemplateByIdQuery.data ?? null;

	const startAutomationForContactMutation = useStartAutomationForContactMutation({
		onError: (error: Api.IOperationResultNoValue) => {
			errorMessages.pushApiError(error);
		},
	});

	const startAutomation = () => {
		if (!automationTemplate || !triggeringOpportunity || !triggeringOpportunity.primaryContact) return;

		const templateName = automationTemplate.name ?? '';
		const contactName = getDisplayName(triggeringOpportunity.primaryContact);

		startAutomationForContactMutation.mutate(
			{ template: automationTemplate, contactId: triggeringOpportunity.primaryContact.id, start: true },
			{
				onSuccess: () => {
					toast.success(`Automation "${templateName}" started for ${contactName}`);
					setTriggeringOpportunity(null);
					setPendingAutomationTemplateId(null);
				},
			}
		);
	};

	const ignoreAutomation = () => {
		setTriggeringOpportunity(null);
		setPendingAutomationTemplateId(null);
	};

	const onEditClicked = () => {
		setShowingEditModal(true);
	};

	const companyVm = React.useMemo(() => {
		if (!opportunity?.company) return null;
		return new CompanyViewModel(userSession, opportunity.company);
	}, [opportunity, userSession]);

	const primaryContactVm = React.useMemo(() => {
		if (!opportunity?.primaryContact) return null;
		return new ContactViewModel(userSession, opportunity.primaryContact);
	}, [opportunity, userSession]);

	const showNotFound = !opportunity && !opportunityQuery.isPending;

	if (showNotFound) return <NotFound />;
	if (!opportunity || opportunityQuery.isPending) {
		return <LoadingSpinner className={css(styleSheet.loading)} type='large' />;
	}

	const onOpportunitySaved = (savedOpportunity: IOpportunity) => {
		const originalStageId = opportunity.boardStage?.id;
		const newStageId = savedOpportunity.boardStage?.id;
		const stageChanged = originalStageId !== newStageId;

		setShowingEditModal(false);

		if (stageChanged && newStageId && userSession?.account?.features?.automation?.enabled) {
			if (!boardsQuery.isSuccess) {
				// Boards with Stage.Tag expansion haven't loaded yet; automation check skipped
				return;
			}
			const board = boards.find((b: Api.IBoard) =>
				(b.stages ?? []).some((s: Api.IBoardStageReference) => s.id === newStageId)
			);
			if (board) {
				const stageName = (board.stages ?? []).find((x: Api.IBoardStageReference) => x.id === newStageId)?.name;
				if (!stageName) return;
				const toStageName = `${board.name}: ${stageName}`;
				const pendingAutomationForStage = stagesWithAutomations.find(
					x => x.name === toStageName && !!x.automationTemplateId
				);

				if (pendingAutomationForStage?.automationTemplateId) {
					setTriggeringOpportunity(savedOpportunity);
					setPendingAutomationTemplateId(pendingAutomationForStage.automationTemplateId);
				}
			}
		}
	};

	const entityForNotes = companyVm || primaryContactVm;

	return (
		<div className={`${css(...(styles || [])) || ''} ${routeContainerClassName || ''} ${className || ''}`}>
			{/** Adds opportunity to location state. Needed so Floating action button can pick up the primary contact from opportunity. */}
			{opportunity && !loc.state ? (
				<Redirect
					to={{
						...loc,
						state: {
							model: opportunity,
						} as ILocationState<undefined, IOpportunity>,
					}}
					push={false}
				/>
			) : null}
			<MultiContainerHeader
				appBarHeader={
					<Breadcrumb>
						<BreadcrumbList>
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to='/dataBoards'>Data Boards</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbLink asChild>
									<NavLink to={`/dataBoards/opportunities/${boardId}`}>
										{getBoardQuery.data?.name ?? 'Opportunities Board'}
									</NavLink>
								</BreadcrumbLink>
							</BreadcrumbItem>
							<BreadcrumbSeparator />
							<BreadcrumbItem>
								<BreadcrumbPage>{opportunityQuery.data?.name}</BreadcrumbPage>
							</BreadcrumbItem>
						</BreadcrumbList>
					</Breadcrumb>
				}
				fullscreenHeader='Opportunity'
			/>
			<SplitView
				className={css(styleSheet.splitView)}
				detail={
					<Tabs.Root defaultValue={OpportunityDetailTabs.Activity} className={css(styleSheet.tabContainer)}>
						<Tabs.List className={css(styleSheet.tabList)}>
							<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={OpportunityDetailTabs.Activity}>
								Activity
							</Tabs.Trigger>
							<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={OpportunityDetailTabs.Notes}>
								Notes
							</Tabs.Trigger>
							{opportunity.company ? (
								<Tabs.Trigger className={css(styleSheet.tabTrigger)} value={OpportunityDetailTabs.Contacts}>
									Contacts
								</Tabs.Trigger>
							) : null}
						</Tabs.List>
						<Tabs.Content value={OpportunityDetailTabs.Activity} className={css(styleSheet.tabContent)}>
							<BoardItemActivityList opportunity={opportunity} />
						</Tabs.Content>
						<Tabs.Content value={OpportunityDetailTabs.Notes} className={css(styleSheet.tabContent)}>
							<EntityRichContent entity={entityForNotes} />
						</Tabs.Content>
						{opportunity.company ? (
							<Tabs.Content value={OpportunityDetailTabs.Contacts} className={css(styleSheet.tabContent)}>
								<CompanyContactsList company={companyVm} />
							</Tabs.Content>
						) : null}
					</Tabs.Root>
				}
				master={
					<div className={css(styleSheet.profile)}>
						{!opportunityQuery.isPending ? (
							<>
								{!opportunity?.isArchived ? (
									<div className={css(styleSheet.profileHeader)}>
										<button className={css(styleSheet.editButton)} onClick={onEditClicked}>
											Edit
										</button>
									</div>
								) : null}
								<OpportunityProfile opportunity={opportunity} />
							</>
						) : null}
					</div>
				}
			/>
			{!!opportunity && !!showingEditModal ? (
				<EditOpportunityModal
					opportunity={opportunity}
					onSave={onOpportunitySaved}
					onCancel={() => setShowingEditModal(false)}
					boards={boards}
				/>
			) : null}

			{userSession?.account?.features?.automation?.enabled && (
				<Modal isOpen={!!pendingAutomationTemplateId} onRequestClose={ignoreAutomation} useDefaultHeader={true}>
					<div className={css(styleSheet.automationPopover)}>
						<div className={css(styleSheet.automationPopoverMessage)}>This tag can trigger an automation flow:</div>
						{getAutomationTemplateByIdQuery.isSuccess ? (
							<>
								<div className={css(styleSheet.automationPopoverName)}>{automationTemplate?.name ?? ''}</div>
								<Button
									kind='primary'
									size='small'
									className={css(styleSheet.automationStartButton)}
									onClick={startAutomation}
									disabled={startAutomationForContactMutation.isPending}
								>
									<span>Start Automation</span>
								</Button>
							</>
						) : getAutomationTemplateByIdQuery.isError ? (
							<div>Failed to load automation details.</div>
						) : (
							<LoadingSpinner type='tiny' />
						)}
					</div>
				</Modal>
			)}
		</div>
	);
};

export const Opportunity = _Opportunity;
